import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../firebase';
import { groupTemplatesByStyle } from '../components/StylePicker';

// Loads the visible prompt templates (server-gated by getPromptTemplates: adminOnly +
// event-exclusive styles), groups them into style groups, and loads the shared tag
// categories + admin ordering. Used by the referral/event map builders so they surface
// the exact same styles as CreatePage without duplicating its loading logic.
export default function useStyleGroups() {
  const [styleGroups, setStyleGroups] = useState([]);
  const [tagCategories, setTagCategories] = useState({});
  const [globalStyleOrder, setGlobalStyleOrder] = useState([]);
  const [hiddenStyles, setHiddenStyles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const getTemplates = httpsCallable(functions, 'getPromptTemplates');
        const [templatesResult, tagsSnap, mapGenSnap] = await Promise.all([
          getTemplates(),
          getDoc(doc(db, 'settings', 'styleTags')),
          getDoc(doc(db, 'settings', 'mapGeneration')),
        ]);
        if (cancelled) return;

        const templates = Array.isArray(templatesResult.data?.templates)
          ? templatesResult.data.templates
          : [];
        setStyleGroups(groupTemplatesByStyle(templates));
        setTagCategories((tagsSnap.exists() && tagsSnap.data().categories) || {});

        const mapGen = mapGenSnap.exists() ? mapGenSnap.data() : {};
        if (Array.isArray(mapGen.globalStyleOrder)) setGlobalStyleOrder(mapGen.globalStyleOrder);
        if (Array.isArray(mapGen.hiddenStyles)) setHiddenStyles(mapGen.hiddenStyles);
      } catch (err) {
        if (cancelled) return;
        console.error('useStyleGroups failed to load styles:', err);
        setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  return { styleGroups, tagCategories, globalStyleOrder, hiddenStyles, isLoading, error };
}
